import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import { TimeFormat } from '~/helpers/utils';
import moment from 'moment-timezone';
import { CreateNotificationOptions, Notification, NotificationSource, NotificationStatus } from '~/types/Notification';
import { OperationDetails } from '~/types/operations';

import { CircularProgress, Tooltip, Typography } from '@h9/ui-lib';
export const NOTIFICATION_MAX_HISTORY = 100;

const SESSION_START_TIME = moment().unix();

/**
 * Get status icon for panel list items
 */
export const getNotificationStatusIcon = (status: string, size = 20) => {
  switch (status) {
    case 'completed':
      return (
        <Tooltip title="Completed">
          <span>
            <CheckCircleIcon color='success' sx={{ fontSize: size }} />
          </span>
        </Tooltip>
      );
    case 'failed':
      return (
        <Tooltip title="Failed">
          <span>
            <ErrorIcon color='error' sx={{ fontSize: size }} />
          </span>
        </Tooltip>
      );
    case 'in-progress':
      return (
        <Tooltip title="In Progress">
          <span>
            <CircularProgress color='primary' size={size} />
          </span>
        </Tooltip>
      );
    case 'queued':
      return (
        <Tooltip title="Queued">
          <span>
            <InfoIcon color='info' sx={{ fontSize: size }} />
          </span>
        </Tooltip>
      );
    case 'cancelled':
      return (
        <Tooltip title="Cancelled">
          <span>
            <InfoIcon color='disabled' sx={{ fontSize: size }} />
          </span>
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Unknown">
          <span>
            <InfoIcon color='disabled' sx={{ fontSize: size }} />
          </span>
        </Tooltip>
      );
  }
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: number): string => {
  const momentInstance = timestamp > 1e12 ? moment(timestamp) : moment.unix(timestamp);
  return momentInstance.format(TimeFormat);
};

export const formatRelativeTime = (timestamp: number): string => {
  const momentInstance = timestamp > 1e12 ? moment(timestamp) : moment.unix(timestamp);
  return momentInstance.fromNow();
};

/**
 * Format completedIn duration for display
 * @param completedIn - Time in seconds
 * @returns Human-readable duration string (e.g., "2m 30s", "1h 15m", "45s")
 */
export const formatCompletedIn = (completedIn?: number): string | null => {
  if (completedIn === undefined || completedIn === null || completedIn < 0) {
    return null;
  }

  const duration = moment.duration(completedIn, 'seconds');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  const parts: string[] = [];
  
  if (hours > 0) {
    parts.push(`${hours}h`);
  }
  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join(' ');
};

export const getDownloadUrlFromOutput = (output: any): string | null => {
  if (!output) return null;
  if (Array.isArray(output)) {
    const item = output.find((entry: any) => entry?.key === 'url' && entry?.value);
    return (item?.value as string) ?? null;
  }
  if (typeof output === 'object' && output !== null && 'url' in output) {
    return (output as Record<string, any>).url as string;
  }
  return null;
}

/**
 * Get human-readable title for operation
 */
export function getOperationTitle(operationType?: string, objectType?: string, objectName?: string): string {
  if (!operationType) return 'Operation in progress';

  const type = operationType.toLowerCase().replace(/_/g, ' ');
  const object = objectType ? ` ${objectType}` : '';
  const name = objectName ? ` (${objectName})` : '';

  // Common operation patterns
  if (type.includes('export')) {
    return `Exporting${object}${name}`;
  }
  if (type.includes('import')) {
    return `Importing${object}${name}`;
  }
  if (type.includes('logs')) {
    return type.charAt(0).toUpperCase() + type.slice(1) + object + name;
  }
  if (type.includes('upgrade') || type.includes('update')) {
    return `Upgrading${object}${name}`;
  }
  if (type.includes('backup')) {
    return `Creating backup${object}${name}`;
  }
  if (type.includes('restore')) {
    return `Restoring${object}${name}`;
  }
  if (type.includes('delete')) {
    return `Deleting${object}${name}`;
  }
  if (type.includes('create')) {
    return `Creating${object}${name}`;
  }
  if (type.includes('capture')) {
    return `Capturing${object}${name}`;
  }
  if (type.includes('bundle')) {
    return `Generating bundle${object}${name}`;
  }
  if (type.includes('simulation') || type.includes('simulate')) {
    return `Running simulation${object}${name}`;
  }

  // Capitalize first letter
  return type.charAt(0).toUpperCase() + type.slice(1) + object + name;
}

/**
 * Get notification type from operation type
 */
export function getNotificationType(operationType?: string): 'operation' | 'download' | 'export' | 'import' | 'manual' {
  if (!operationType) return 'operation';

  const type = operationType.toLowerCase();

  if (type.includes('export')) return 'export';
  if (type.includes('import')) return 'import';
  if (type.includes('download')) return 'download';

  return 'operation';
}

/**
 * Utility functions for managing notification panel state in localStorage
 */

const NOTIFICATION_PANEL_KEY = 'notification_panel_last_opened';
/**
 * Get the timestamp when the notification panel was last opened
 * @returns Unix timestamp in milliseconds, or null if never opened
 */

export const getNotificationPanelLastOpenedTime = (): number | null => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_PANEL_KEY);
    if (stored) {
      const timestamp = parseInt(stored, 10);
      return isNaN(timestamp) ? null : timestamp;
    }
    return null;
  } catch (error) {
    console.error('Error reading notification panel last opened time:', error);
    return null;
  }
};

/**
 * Save the current timestamp as the last opened time for the notification panel
 */

export const setNotificationPanelLastOpenedTime = (): void => {
  try {
    const now = moment().unix();
    localStorage.setItem(NOTIFICATION_PANEL_KEY, now.toString());
  } catch (error) {
    console.error('Error saving notification panel last opened time:', error);
  }
};
/**
 * Check if a notification should be marked as read based on when panel was last opened
 * @param notificationTimestamp - Notification timestamp (unix seconds)
 * @param lastOpenedTime - Last panel opened time in unix seconds (from localStorage)
 * @returns true if notification should be read, false if unread
 */

export const shouldNotificationBeRead = (
  status: NotificationStatus,
  notificationTimestamp: number,
  lastOpenedTime: number | null = getNotificationPanelLastOpenedTime()
): boolean => {
  // Normalize timestamp to seconds if it's in milliseconds
  const timestampInSeconds = notificationTimestamp > 1e12 ? notificationTimestamp / 1000 : notificationTimestamp;

  // 1. it will be unread if in-progress
  if (status === 'queued' || status === 'in-progress') {
    return false;
  }

  // 2. It will be unread if it is created / updated after the login / session start
  if (timestampInSeconds > SESSION_START_TIME) {
    return false;
  }

  if (lastOpenedTime === null) {
    // Panel never opened, and it's an old notification (before session start)
    // and not in-progress (checked above), mark it as read.
    return true;
  }

  // Notification is read if it was created before the last panel open or if it is completed/failed
  return timestampInSeconds <= lastOpenedTime || status === 'completed' || status === 'failed';
};

export function createNotificationToast(notification: Notification){
  return (
    <>
      <Typography variant="body1">{notification.title}</Typography>
      <Typography variant="body2">{notification.subtitle}</Typography>     
    </>
  );
}

export function createNotification(data: CreateNotificationOptions){
  const timestamp = data.timestamp ?? Date.now();
  const isRead = data.read ?? (
    data.read === undefined 
      ? shouldNotificationBeRead(data.status ?? 'in-progress', timestamp, getNotificationPanelLastOpenedTime())
      : false
  );
  const id = data.id ?? `notification-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  const notification: Notification = {
    id,
    type: data.type,
    title: data.title,
    subtitle: data.subtitle,
    status: data.status ?? 'in-progress',
    progress: data.progress,
    operationId: data.operationId,
    timestamp,
    completedIn: data.completedIn,
    dismissible: data.dismissible ?? true,
    autoDismiss: data.autoDismiss,
    actions: data.actions ?? [],
    metadata: data.metadata ?? {},
    read: isRead,
    source: data.source ?? 'manual',
  };
  return notification;
}

/**
 * Map operation status to notification status
 */
export const mapOperationStatus = (operationStatus: string): NotificationStatus => {
  switch (operationStatus?.toUpperCase()) {
    case 'SCHEDULED':
    case 'QUEUED':
    case 'ACKNOWLEDGE':
    case 'ACKNOWLEDGED':
      return 'queued';
    case 'IN_PROGRESS':
      return 'in-progress';
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
    default:
      return 'in-progress';
  }
};

/**
 * Create a notification from an operation
 */
export function createNotificationFromOperation(
  operation: OperationDetails,
  source: NotificationSource = 'initial-load',
): Notification {
  const title = getOperationTitle(operation.operationType, operation.objectType, operation.objectName);
  const operationStatus = operation.status || '';
  const mappedStatus = mapOperationStatus(operationStatus);
  
  // Calculate completedIn for completed/failed/cancelled operations
  let completedIn: number | undefined = undefined;
  if (mappedStatus === 'completed' || mappedStatus === 'failed' || mappedStatus === 'cancelled') {
    const completionTime = operation.completionTime ?? operation.updationTime;
    const creationTime = operation.creationTime;
    if (completionTime && creationTime && completionTime > 0 && creationTime > 0) {
      completedIn = Math.max(0, completionTime - creationTime);
    }
  }

  // Use the most recent timestamp available for determining read status
  const notificationTimestamp = operation.completionTime ?? operation.updationTime ?? operation.creationTime ?? (Date.now() / 1000);
  
  // Determine read status
  const lastOpenedTime = getNotificationPanelLastOpenedTime();
  const isRead = shouldNotificationBeRead(mappedStatus, notificationTimestamp, lastOpenedTime);

  const autoDismiss = mappedStatus === 'completed' || mappedStatus === 'cancelled';

  return createNotification({
    id: `notify-${operation.id}`,
    type: getNotificationType(operation.operationType),
    title,
    subtitle:  operation.statusMessage || undefined,
    status: mappedStatus,
    operationId: operation.id,
    dismissible: true,
    autoDismiss,
    timestamp: notificationTimestamp,
    completedIn,
    read: isRead,
    source,
    metadata: {
      output: operation.output,
      input: operation.input,
    },
  });
}

export function generateNotificationsFromOperations(operations: OperationDetails[], source : NotificationSource = 'initial-load') {
  // get the latest MAX_HISTORY operations
  const latestOperations = operations.toSorted((a, b) => (b.creationTime ?? 0) - (a.creationTime ?? 0)).slice(0, NOTIFICATION_MAX_HISTORY);

  const notifications: Notification[] = latestOperations.map((operation) => {
    return createNotificationFromOperation(operation, source);
  });

  return notifications;
}